/**
 * Dynamically sets Open Graph / Twitter Card meta tags for the current page.
 * When WhatsApp (or any social app) fetches the shared URL, these tags produce
 * a rich preview card with image, title, and description.
 *
 * Note: WhatsApp's crawler can only read OG tags from SSR/statically rendered HTML.
 * For a pure SPA like this, the tags help with:
 * - Twitter/Facebook share cards (they follow JS redirects)
 * - Native share sheet preview text on iOS/Android
 * - Browser tab metadata
 *
 * For full WhatsApp preview support, a server-side OG tag injector
 * (e.g. Cloudflare Worker or nginx sub_filter) is needed.
 */
import { useEffect } from 'react';

type ContentMeta = {
  title: string;
  description?: string;
  imageUrl?: string;
  url?: string;
  type?: string;
};

function setMetaTag(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMetaTag(property: string) {
  const el = document.querySelector(`meta[property="${property}"]`);
  if (el) el.remove();
}

const OG_PROPERTIES = [
  'og:title',
  'og:description',
  'og:image',
  'og:url',
  'og:type',
  'og:site_name',
];

export function useContentMetaTags(meta?: ContentMeta) {
  useEffect(() => {
    if (!meta?.title) return;

    setMetaTag('og:title', meta.title);
    setMetaTag('og:site_name', 'KINASTIC');
    setMetaTag('og:type', meta.type || 'article');
    setMetaTag('og:url', meta.url || window.location.href);

    if (meta.description) {
      setMetaTag('og:description', meta.description);
    }
    if (meta.imageUrl) {
      setMetaTag('og:image', meta.imageUrl);
    }

    return () => {
      OG_PROPERTIES.forEach(removeMetaTag);
    };
  }, [meta?.title, meta?.description, meta?.imageUrl, meta?.url, meta?.type]);
}
